import { Box, Button, Icon, IconButton, Stack, Typography } from '@mui/material';
import { AttendanceFrequencyInput, FrequencyTypeEnum, ResponsibleTypeEnum } from '@schooly/api';
import { DropdownSelect, DropdownSelectRow } from '@schooly/components/filters';
import {
  Attention2Icon,
  CheckIcon,
  CrossIcon,
  ModalConfirm,
  ModalHeader,
  ModalMain,
  WarningBlock,
} from '@schooly/style';
import { formatListWithAnd } from '@schooly/utils/common';
import { FC, useMemo, useRef } from 'react';
import { Control, Controller, FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ModalContent,
  ModalFooter,
} from '../../../../components/uikit-components/Modal/Modal.styled';
import { getTimetableWarningItems, TimetableWarningItem } from './am-pm-helpers';

export interface AmPmAgeGroup {
  id: string;
  name: string;
}

type AttendanceFrequencyDraft =
  | {
      frequency: FrequencyTypeEnum.Am;
      am_responsible: ResponsibleTypeEnum | null;
      per_period_enabled: boolean;
    }
  | {
      frequency: FrequencyTypeEnum.AmPm;
      am_responsible: ResponsibleTypeEnum | null;
      pm_responsible: ResponsibleTypeEnum | null;
      per_period_enabled: boolean;
    };

interface AmPmFormRow {
  id: string;
  name: string;
  attendance_frequency: AttendanceFrequencyDraft | null;
}

interface AmPmFormValues {
  rows: AmPmFormRow[];
}

interface Props {
  ageGroups: AmPmAgeGroup[];
  onCancel: () => void;
  onSave: (frequencyById: Record<string, AttendanceFrequencyInput>) => void;
  saving?: boolean;
}

// Picker exposes AM and AM/PM only (never PM-only). See global constraints.
const FREQUENCY_OPTIONS: (FrequencyTypeEnum.Am | FrequencyTypeEnum.AmPm)[] = [
  FrequencyTypeEnum.Am,
  FrequencyTypeEnum.AmPm,
];

const RESPONSIBLE_OPTIONS: ResponsibleTypeEnum[] = [
  ResponsibleTypeEnum.Tutor,
  ResponsibleTypeEnum.SubjectTeacher,
];

const PER_PERIOD_OPTIONS: { value: boolean; labelId: string }[] = [
  { value: false, labelId: 'school-tabs-AgeGroups-Amp-PerPeriod-Disabled' },
  { value: true, labelId: 'school-tabs-AgeGroups-Amp-PerPeriod-Enabled' },
];

const FREQUENCY_LABEL_IDS: Record<FrequencyTypeEnum.Am | FrequencyTypeEnum.AmPm, string> = {
  [FrequencyTypeEnum.Am]: 'school-tabs-AgeGroups-Amp-Frequency-Am',
  [FrequencyTypeEnum.AmPm]: 'school-tabs-AgeGroups-Amp-Frequency-AmPm',
};

const RESPONSIBLE_LABEL_IDS: Record<ResponsibleTypeEnum, string> = {
  [ResponsibleTypeEnum.Tutor]: 'school-tabs-AgeGroups-Amp-Responsible-Tutor',
  [ResponsibleTypeEnum.SubjectTeacher]: 'school-tabs-AgeGroups-Amp-Responsible-SubjectTeacher',
};

const WARNING_PART_LABEL_IDS: Record<TimetableWarningItem, string> = {
  am: 'school-tabs-AgeGroups-Amp-Warning-Part-Am',
  pm: 'school-tabs-AgeGroups-Amp-Warning-Part-Pm',
  per_period: 'school-tabs-AgeGroups-Amp-Warning-Part-PerPeriod',
};

const toFrequencyInput = (
  value: AttendanceFrequencyDraft | null,
): AttendanceFrequencyInput | null => {
  if (!value) return null;
  if (value.frequency === FrequencyTypeEnum.AmPm) {
    if (value.am_responsible === null || value.pm_responsible === null) return null;
    return {
      frequency: value.frequency,
      am_responsible: value.am_responsible,
      pm_responsible: value.pm_responsible,
      per_period_enabled: value.per_period_enabled,
    };
  }
  if (value.am_responsible === null) return null;
  return {
    frequency: value.frequency,
    am_responsible: value.am_responsible,
    per_period_enabled: value.per_period_enabled,
  };
};

const isCompleteFrequency = (value: AttendanceFrequencyDraft | null): boolean =>
  toFrequencyInput(value) !== null;

interface AmPmModalRowProps {
  index: number;
  name: string;
  control: Control<AmPmFormValues>;
}

const AmPmModalRow: FC<AmPmModalRowProps> = ({ index, name, control }) => {
  const { formatMessage } = useIntl();
  const frequencyRef = useRef<DropdownSelect | null>(null);
  const amRef = useRef<DropdownSelect | null>(null);
  const pmRef = useRef<DropdownSelect | null>(null);
  const perPeriodRef = useRef<DropdownSelect | null>(null);

  return (
    <Stack gap={1}>
      <Typography variant="h2">{name}</Typography>
      <Controller
        control={control}
        name={`rows.${index}.attendance_frequency`}
        rules={{
          validate: (value) => isCompleteFrequency(value as AttendanceFrequencyDraft | null),
        }}
        render={({ field }) => {
          const v = field.value;

          const onFrequencyChange = (nextType: FrequencyTypeEnum.Am | FrequencyTypeEnum.AmPm) => {
            field.onChange(
              nextType === FrequencyTypeEnum.AmPm
                ? {
                    frequency: nextType,
                    am_responsible: v?.am_responsible ?? null,
                    pm_responsible: v && 'pm_responsible' in v ? v.pm_responsible : null,
                    per_period_enabled: v?.per_period_enabled ?? false,
                  }
                : {
                    frequency: nextType,
                    am_responsible: v?.am_responsible ?? null,
                    per_period_enabled: v?.per_period_enabled ?? false,
                  },
            );
          };

          const warningItems = v ? getTimetableWarningItems(v) : [];
          const warningList = formatListWithAnd(
            warningItems.map((item) => formatMessage({ id: WARNING_PART_LABEL_IDS[item] })),
            formatMessage,
          );

          return (
            <Stack gap={1}>
              <Stack direction="row" gap={1}>
                <Box flex={1} minWidth={0}>
                  <DropdownSelect
                    ref={frequencyRef}
                    placeholder={formatMessage({ id: 'school-tabs-AgeGroups-Amp-Frequency' })}
                    hasValues={!!v?.frequency}
                    renderContent={() => (
                      <Stack p={1}>
                        {FREQUENCY_OPTIONS.map((opt) => (
                          <DropdownSelectRow
                            key={opt}
                            isSelected={v?.frequency === opt}
                            onClick={() => {
                              onFrequencyChange(opt);
                              frequencyRef.current?.close();
                            }}
                          >
                            {formatMessage({ id: FREQUENCY_LABEL_IDS[opt] })}
                          </DropdownSelectRow>
                        ))}
                      </Stack>
                    )}
                  >
                    {() =>
                      v?.frequency ? (
                        <Typography variant="h3" noWrap>
                          {formatMessage({ id: FREQUENCY_LABEL_IDS[v.frequency] })}
                        </Typography>
                      ) : null
                    }
                  </DropdownSelect>
                </Box>

                <Box flex={1} minWidth={0}>
                  <DropdownSelect
                    ref={amRef}
                    disabled={!v}
                    placeholder={formatMessage({ id: 'school-tabs-AgeGroups-Amp-Responsible-Am' })}
                    hasValues={!!v?.am_responsible}
                    renderContent={() => (
                      <Stack p={1}>
                        {RESPONSIBLE_OPTIONS.map((opt) => (
                          <DropdownSelectRow
                            key={opt}
                            isSelected={v?.am_responsible === opt}
                            onClick={() => {
                              field.onChange({
                                ...(v as AttendanceFrequencyDraft),
                                am_responsible: opt,
                              });
                              amRef.current?.close();
                            }}
                          >
                            {formatMessage({ id: RESPONSIBLE_LABEL_IDS[opt] })}
                          </DropdownSelectRow>
                        ))}
                      </Stack>
                    )}
                  >
                    {() =>
                      v?.am_responsible ? (
                        <Typography variant="h3" noWrap>
                          {formatMessage({ id: RESPONSIBLE_LABEL_IDS[v.am_responsible] })}
                        </Typography>
                      ) : null
                    }
                  </DropdownSelect>
                </Box>

                {v?.frequency === FrequencyTypeEnum.AmPm && (
                  <Box flex={1} minWidth={0}>
                    <DropdownSelect
                      ref={pmRef}
                      placeholder={formatMessage({
                        id: 'school-tabs-AgeGroups-Amp-Responsible-Pm',
                      })}
                      hasValues={!!v.pm_responsible}
                      renderContent={() => (
                        <Stack p={1}>
                          {RESPONSIBLE_OPTIONS.map((opt) => (
                            <DropdownSelectRow
                              key={opt}
                              isSelected={v.pm_responsible === opt}
                              onClick={() => {
                                field.onChange({ ...v, pm_responsible: opt });
                                pmRef.current?.close();
                              }}
                            >
                              {formatMessage({ id: RESPONSIBLE_LABEL_IDS[opt] })}
                            </DropdownSelectRow>
                          ))}
                        </Stack>
                      )}
                    >
                      {() =>
                        v.pm_responsible ? (
                          <Typography variant="h3" noWrap>
                            {formatMessage({ id: RESPONSIBLE_LABEL_IDS[v.pm_responsible] })}
                          </Typography>
                        ) : null
                      }
                    </DropdownSelect>
                  </Box>
                )}

                <Box flex={1} minWidth={0}>
                  <DropdownSelect
                    ref={perPeriodRef}
                    disabled={!v}
                    placeholder={formatMessage({ id: 'school-tabs-AgeGroups-Amp-PerPeriod' })}
                    hasValues={!!v}
                    renderContent={() => (
                      <Stack p={1}>
                        {PER_PERIOD_OPTIONS.map((opt) => (
                          <DropdownSelectRow
                            key={opt.labelId}
                            isSelected={!!v && v.per_period_enabled === opt.value}
                            onClick={() => {
                              field.onChange({
                                ...(v as AttendanceFrequencyDraft),
                                per_period_enabled: opt.value,
                              });
                              perPeriodRef.current?.close();
                            }}
                          >
                            {formatMessage({ id: opt.labelId })}
                          </DropdownSelectRow>
                        ))}
                      </Stack>
                    )}
                  >
                    {() =>
                      v ? (
                        <Typography variant="h3" noWrap>
                          {formatMessage({
                            id: v.per_period_enabled
                              ? 'school-tabs-AgeGroups-Amp-PerPeriod-Enabled'
                              : 'school-tabs-AgeGroups-Amp-PerPeriod-Disabled',
                          })}
                        </Typography>
                      ) : null
                    }
                  </DropdownSelect>
                </Box>
              </Stack>

              {warningItems.length > 0 && (
                <WarningBlock>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Icon sx={{ color: 'background.paper', path: { color: 'common.orange' } }}>
                      <Attention2Icon />
                    </Icon>
                    <Typography variant="h3" color="inherit">
                      <FormattedMessage
                        id="school-tabs-AgeGroups-Amp-Warning-Registers"
                        values={{ items: warningList }}
                      />
                    </Typography>
                  </Stack>
                </WarningBlock>
              )}
            </Stack>
          );
        }}
      />
    </Stack>
  );
};

export const SchoolGeneralAgeGroupsAmPmModal: FC<Props> = ({
  ageGroups,
  onCancel,
  onSave,
  saving,
}) => {
  const { formatMessage } = useIntl();

  const defaultValues = useMemo<AmPmFormValues>(
    () => ({
      rows: ageGroups.map((ag) => ({
        id: ag.id,
        name: ag.name,
        attendance_frequency: {
          frequency: FrequencyTypeEnum.Am,
          am_responsible: ResponsibleTypeEnum.Tutor,
          per_period_enabled: false,
        },
      })),
    }),
    [ageGroups],
  );

  const form = useForm<AmPmFormValues>({ defaultValues, mode: 'onChange' });

  const handleSubmit = form.handleSubmit((values) => {
    const byId: Record<string, AttendanceFrequencyInput> = {};
    for (const r of values.rows) {
      const input = toFrequencyInput(r.attendance_frequency);
      if (input) byId[r.id] = input;
    }
    onSave(byId);
  });

  return (
    <ModalConfirm
      open
      fullWidth
      width={750}
      onClose={onCancel}
      sx={(theme) => ({
        zIndex: theme.zIndex.modal,
        // Keep header and footer pinned: the paper itself must not scroll,
        // only ModalMain does.
        '& .MuiDialog-paper': {
          maxHeight: 'calc(100% - 64px)',
          overflow: 'hidden',
        },
        '& .MuiDialog-paper > form': {
          minHeight: 0,
          overflow: 'hidden',
        },
      })}
    >
      <ModalHeader
        active
        multiline
        title={formatMessage({
          id:
            ageGroups.length === 1
              ? 'school-tabs-AgeGroups-Amp-Title'
              : 'school-tabs-AgeGroups-Amp-TitlePlural',
        })}
      >
        <IconButton onClick={onCancel}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <ModalMain sx={{ minHeight: 0 }}>
            {/* ModalContent defaults to height:100%, which here resolves to its
                own content height and kills the scrollbar. Let it stretch instead. */}
            <ModalContent active sx={{ height: 'auto', minHeight: 0 }}>
              <Stack gap={3}>
                {ageGroups.map((row, i) => (
                  <AmPmModalRow key={row.id} index={i} name={row.name} control={form.control} />
                ))}
              </Stack>
            </ModalContent>
          </ModalMain>
          <ModalFooter active sx={{ justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              disabled={!form.formState.isValid || saving}
              endIcon={<CheckIcon />}
            >
              <FormattedMessage id="action-Save" />
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalConfirm>
  );
};
